import React, { useEffect, useState } from "react";
import styled from "styled-components";
import apiFetch from "../../util/fetch-utils";
import Select from "react-select";
import { filterByNameOrId } from "../optionFilter";
import { portalledMenu } from "../portalledMenu";
import { GoogleAccountSelect, tokenOption } from "../GoogleAccountSelect";
import { useLocation } from "react-router";
import { SettingsDialog, Field, FieldLabel, ErrorMsg, WarningNote } from "../SettingsDialog";
import { savedSelection } from "./savedSelection";

const IdChip = styled.span`
    display: inline-block;
    background: #f1f5f9;
    color: #475569;
    font-size: 0.72rem;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 4px;
    font-family: monospace;
    margin-right: 6px;
`;

function accountOption(summary) {
    return {
        label: <><IdChip>{summary.account.name.replace("accounts/", "")}</IdChip>{summary.account.displayName}</>,
        value: summary.account.name,
        displayName: summary.account.displayName,
    };
}

function propertyOption(property) {
    return {
        label: <><IdChip>{property.name.replace("properties/", "")}</IdChip>{property.displayName}</>,
        value: property.name,
        displayName: property.displayName,
    };
}

/* ── Component ───────────────────────────────────────────────── */
export const AnalyticsSettingsModal = (props) => {
    const analyticsSettings = props.analyticsSettings || {};

    const [analyticsData,     setAnalyticsData]     = useState([]);
    const [analyticsToken,    setAnalyticsToken]    = useState(null);
    const [analyticsAccount,  setAnalyticsAccount]  = useState(null);
    const [analyticsProperty, setAnalyticsProperty] = useState(null);
    const [loading,           setLoading]           = useState(false);
    const [loadingFailure,    setLoadingFailure]    = useState(null);
    const [userHasChosen,     setUserHasChosen]     = useState(false);

    const location = useLocation();
    const tokenName = analyticsToken?.value;

    // The dialog shows what is saved until the user picks something else, and reopening it starts
    // from what is saved again. The account and the property can only be matched once the summaries
    // have arrived, which is often after the dialog is already open, so this runs again when they do.
    useEffect(() => {
        if (!props.isOpen) return setUserHasChosen(false);
        if (userHasChosen) return;
        if (getParam(location.search, "newTokenName")) return;

        const saved = savedSelection(analyticsSettings, props.userDetails.analyticsTokens, analyticsData);
        setAnalyticsToken(saved.token ? tokenOption(saved.token) : null);
        setAnalyticsAccount(saved.summary ? accountOption(saved.summary) : null);
        setAnalyticsProperty(saved.property ? propertyOption(saved.property) : null);
    }, [props.isOpen, userHasChosen, analyticsData, props.userDetails.analyticsTokens,
        analyticsSettings.tokenName, analyticsSettings.accountId, analyticsSettings.propertyId]);

    useEffect(() => {
        if (!props.userDetails.analyticsTokens) return;
        const newTokenName = getParam(location.search, "newTokenName");
        const t = props.userDetails.analyticsTokens.find(tok => tok.name === newTokenName);
        if (t) setAnalyticsToken(tokenOption(t));
    }, [location, props.userDetails.analyticsTokens]);

    useEffect(() => {
        if (!tokenName) return;
        setLoading(true);
        setAnalyticsData([]);
        setLoadingFailure(null);
        apiFetch(`/api/google/analytics/account-summaries?tokenName=${tokenName}`)
            .then(res => res.json().catch(() => ({})).then(body => {
                if (res.status !== 200) return setLoadingFailure(failureReading(body.reason));
                setAnalyticsData(body);
            }))
            .catch(() => setLoadingFailure(failureReading()))
            .finally(() => setLoading(false));
    }, [tokenName, analyticsToken?.id]);

    const accountOptions  = analyticsData.map(accountOption);
    const propertyOptions = analyticsAccount?.value
        ? (analyticsData.find(s => s.account.name === analyticsAccount.value)?.properties || []).map(propertyOption)
        : [];

    return (
        <SettingsDialog
            title="Google Analytics connection"
            workspace={props.workspace}
            isOpen={props.isOpen}
            onClose={props.toggle}
            onSave={onOk}
            saveDisabled={!analyticsProperty?.value}
        >
                    <Field>
                        <FieldLabel>Google account</FieldLabel>
                        <GoogleAccountSelect
                            value={analyticsToken}
                            onChange={data => { setUserHasChosen(true); setLoadingFailure(null); setAnalyticsToken(data); setAnalyticsAccount(null); setAnalyticsProperty(null); }}
                            redirectUrl={urlThatReopensThisDialog()}
                        />
                        {loadingFailure?.kind === "note" && <WarningNote>{loadingFailure.message}</WarningNote>}
                        {loadingFailure?.kind === "error" && <ErrorMsg>{loadingFailure.message}</ErrorMsg>}
                    </Field>

                    <Field>
                        <FieldLabel>Account</FieldLabel>
                        <Select
                            {...portalledMenu}
                            isClearable
                            isLoading={loading}
                            isDisabled={loadingFailure !== null}
                            value={analyticsAccount}
                            onChange={data => { setUserHasChosen(true); setAnalyticsAccount(data); setAnalyticsProperty(null); }}
                            options={accountOptions}
                            filterOption={filterByNameOrId}
                            placeholder="Select an account…"
                        />
                    </Field>

                    <Field>
                        <FieldLabel>Property</FieldLabel>
                        <Select
                            {...portalledMenu}
                            isClearable
                            isLoading={loading}
                            isDisabled={loadingFailure !== null}
                            value={analyticsProperty}
                            onChange={data => { setUserHasChosen(true); setAnalyticsProperty(data); }}
                            options={propertyOptions}
                            filterOption={filterByNameOrId}
                            placeholder="Select a property…"
                        />
                    </Field>
        </SettingsDialog>
    );

    // Connecting an account leaves the app for Google's consent screen, so the return URL carries the
    // flag that reopens this dialog on the way back.
    function urlThatReopensThisDialog() {
        const curUrl = new URL(window.location.href);
        const curHash = curUrl.hash;
        curUrl.hash = curHash + (curHash.includes("?") ? "&" : "?") + "selectAnalyticsViewOpen=true";
        return curUrl.toString();
    }

    // The backend answers 403 and a reason whenever the connection is there but cannot serve the
    // call. Both reasons are the user's to put right, so they read as a note rather than a failure.
    function failureReading(reason) {
        if (reason === "SCOPE_NOT_GRANTED") return {
            kind: "note",
            message: "Google Analytics access has not been granted for this Google account. Reconnect it with the + button and allow Google Analytics when Google asks, or pick a different account.",
        };
        if (reason === "AUTHORIZATION_REJECTED") return {
            kind: "note",
            message: "Google no longer accepts this connection, so it has to be granted again. Reconnect the account with the + button, or pick a different account.",
        };
        return { kind: "error", message: "Unable to read this Google Analytics connection. Pick another account, or connect one with +." };
    }

    function getParam(query, paramName) {
        if (!query.includes("?") || !query.includes(paramName)) return;
        return query.slice(1).split("&").map(v => v.split("=")).find(v => v[0] === paramName)?.[1];
    }

    function onOk() {
        props.setAnalyticsSettings({
            tokenName:             analyticsToken.value,
            analyticsAccountOwner: analyticsToken.analyticsAccountOwner,
            accountId:             analyticsAccount.value.replace("accounts/", ""),
            accountDisplayName:    analyticsAccount.displayName,
            propertyId:            analyticsProperty.value.replace("properties/", ""),
            propertyDisplayName:   analyticsProperty.displayName,
        });
        props.toggle();
    }
};

AnalyticsSettingsModal.defaultProps = {
    analyticsSettings: {},
    userDetails: { analyticsTokens: [] },
};
